'use client';

import {useTranslations} from 'next-intl';
import {ChangeEvent, useMemo, useState} from 'react';
import {Checkbox} from '../../../../../../components/design-system';
import useShop from '../../../../../../stores/shop/useContext';
import useFilters from '../hooks/useFilters';
import FilterModal from './FilterModal';

export default function FilterLocation() {
  const t = useTranslations('shop');

  const {facets} = useShop((s) => s);
  const {filters, set} = useFilters();

  const [selected, setSelected] = useState({
    country: filters.country || [],
    region: filters.region || [],
  });
  const appliedValue = useMemo(() => {
    const country =
      !!filters.country && filters.country.length > 0
        ? facets.countries.find((f) => f.slug === filters.country![0])
        : undefined;
    const region =
      !!filters.country && filters.country.length > 0
        ? facets.countries
            .reduce((acc, curr) => [...acc, ...curr.children], [])
            .find((f) => f.slug === filters.region![0])
        : undefined;

    if (!country && !region) {
      return undefined;
    }

    return [
      country?.name || region?.name,
      (filters.country || []).length + (filters.region || []).length > 1
        ? ` +${
            (filters.country || []).length + (filters.region || []).length - 1
          }`
        : '',
    ].join('');
  }, [filters.country, filters.region]);

  function handleChangeCountry(event: ChangeEvent<HTMLInputElement>) {
    if (selected.country.includes(event.target.value)) {
      setSelected((prev) => ({
        ...prev,
        country: Array.from(
          new Set(prev.country.filter((i) => i !== event.target.value)),
        ),
      }));
      return;
    }

    setSelected((prev) => ({
      ...prev,
      country: Array.from(new Set([...prev.country, event.target.value])),
    }));
  }

  function handleChangeRegion(event: ChangeEvent<HTMLInputElement>) {
    if (selected.region.includes(event.target.value)) {
      setSelected((prev) => ({
        ...prev,
        region: Array.from(
          new Set(prev.region.filter((i) => i !== event.target.value)),
        ),
      }));
      return;
    }

    setSelected((prev) => ({
      ...prev,
      region: Array.from(new Set([...prev.region, event.target.value])),
    }));
  }

  function handleApply() {
    set({country: selected.country, region: selected.region});
  }

  return (
    <FilterModal
      label={t('filter.country.title')}
      appliedValue={appliedValue}
      onApply={() => handleApply()}
    >
      <form className="px-4 space-y-2">
        {(facets.countries || []).map((item) => (
          <div key={item.id}>
            <Checkbox
              name="country"
              onChange={handleChangeCountry}
              checked={selected.country.includes(item.slug)}
              value={item.slug}
              label={item.name}
              reverse
            />
            {item.children.map((child) => (
              <div key={child.id} className="pl-4 mt-2">
                <Checkbox
                  name="region"
                  onChange={handleChangeRegion}
                  checked={selected.region.includes(child.slug)}
                  value={child.slug}
                  label={child.name}
                  reverse
                />
              </div>
            ))}
          </div>
        ))}
      </form>
    </FilterModal>
  );
}
